import {
    fetchPutState,
    GetStateByStateKeyResponse
} from '@iwoca/lapi-client/edge';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';

import { useStateKey } from '../../../../iwQueryClient/useStateKey';
import { TDeserialisedPeople } from '../../../modals/idCheckVerificationModal/IdCheckVerificationModal.types';

type AddPhoneNumberParams = {
    person: TDeserialisedPeople[number];
    phoneNumber: string;
};
type TCustomerStatePeople = NonNullable<
    GetStateByStateKeyResponse['data']['application']
>['people'];

export const useAddPhoneNumber = () => {
    const stateKey = useStateKey();
    const queryClient = useQueryClient();

    const mutation = useMutation({
        mutationFn: ({ person, phoneNumber }: AddPhoneNumberParams) => {
            const customerState =
                queryClient.getQueryData<GetStateByStateKeyResponse>([
                    'customer_state',
                    stateKey
                ]);

            if (!customerState?.data?.application?.people) {
                throw new Error('Customer state not found');
            }

            const updatedPeople = customerState.data.application.people.map(
                (p: NonNullable<TCustomerStatePeople>[number]) => {
                    if (p.uid === person.uid) {
                        const phones = p.phones || [];
                        return {
                            ...p,
                            phones: [
                                ...phones,
                                {
                                    uid: uuidv4(),
                                    number: phoneNumber,
                                    type:
                                        phones.length === 0
                                            ? 'primary'
                                            : 'secondary'
                                }
                            ]
                        };
                    }
                    return p;
                }
            );

            const updatedData = {
                ...customerState,
                application: {
                    ...customerState.data.application,
                    people: updatedPeople
                }
            } as GetStateByStateKeyResponse['data'];

            return fetchPutState({
                stateKey,
                body: { data: { ...updatedData } }
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['customer_state', stateKey]
            });
        }
    });

    return {
        addPhoneNumber: mutation.mutateAsync,
        isLoading: mutation.isLoading,
        error: mutation.error
    };
};
